/**
 * Report a frontend error to the backend so it is stored in error_reports (admin view).
 * Uses POST /api/report-error. Does not throw; failures are ignored so the UI can continue.
 */

import { safeTrim } from './utils';
import { getWorkerApiBase } from './worker-api';

export type ReportErrorContext = Record<string, unknown>;

/**
 * Send error to backend for storage in error_reports. Fire-and-forget; never throws.
 */
export function reportErrorToBackend(
  message: string,
  options?: { stack?: string; context?: ReportErrorContext }
): void {
  const body = {
    level: 'error' as const,
    message: safeTrim(message) || 'Unknown error',
    stack: options?.stack,
    context: options?.context,
  };
  fetch(`${getWorkerApiBase()}/api/report-error`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => {
    // Ignore network errors; do not mask the original error in the UI
  });
}

/**
 * Extract a readable message from any thrown value (Error, PostgrestError, etc.).
 */
function getErrorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  if (e != null && typeof e === 'object') return JSON.stringify(e);
  return String(e);
}

/**
 * Report an unknown caught error (e.g. from catch (e)) with optional context.
 * Ensures Supabase/PostgREST errors (code, message) are stored even if context is truncated.
 */
export function reportCaughtError(
  e: unknown,
  context?: ReportErrorContext
): void {
  let message = getErrorMessage(e);
  const stack = e instanceof Error ? e.stack : undefined;
  const ctx: ReportErrorContext = { ...context };
  if (e && typeof e === 'object' && 'code' in e) {
    const code = (e as { code: unknown }).code;
    ctx.code = code;
    if (code != null && String(code).trim() !== '') message = `[${code}] ${message}`;
  }
  if (e && typeof e === 'object' && 'details' in e) ctx.details = (e as { details: unknown }).details;
  if (e && typeof e === 'object' && 'hint' in e) ctx.hint = (e as { hint: unknown }).hint;
  reportErrorToBackend(message, { stack, context: Object.keys(ctx).length ? ctx : context });
}
